import { observable, computed, action, toJS, makeObservable } from 'mobx'
import {
  FRAME_COLORS,
  MATTING,
  FILTERS,
  TILE_CONTENT,
  type MatColorKey,
  type TILE_SIZES,
  type MATERIAL_TYPES,
  DEFAULT_MAT_COLOR,
} from '@mixtiles/web-backend-shared'
import { getTileCropTargetAspectRatio } from 'utils/cropRatioUtils'
import { isFramedMaterial } from 'utils/materialTypeUtils'
import { type CropperData, type StylingFields } from '../TilesStore.types'
import { type CropAlignment } from 'utils/cropAlignment.consts'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'
import type { TileCrop, TileData } from './tileSchemas'
import { logger } from 'services/logger'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import {
  baseTileFields,
  CustomStyleSchema,
  MattingColorSchema,
} from './tileSchemas'
import { type TilePatch, type TilePatchMode } from '../TilePatch'
import { getTileStyleData } from 'services/tilesStyling'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { experimentManager } from 'services/ExperimentManager/ExperimentManager'

const BASE_PERSISTENCE_KEYS = [
  'id',
  'cropParams',
  'tileSize',
  'uploadedAt',
  'metadata',
  'materialType',
  'customStyle',
  'originalUrl',
  'tileContent',
  'currentAspectRatio',
]

export type TilePhotoUrl = { uid: string; url: string | undefined }
export type TilePersistenceDisposition = 'skip' | 'pending' | 'ready'
export type TileCropAdjustmentContext = {
  uid?: string
  width: number
  height: number
  initialCropAlignment?: CropAlignment
}

const IGNORED_UPDATE_PATCH_KEYS = new Set([
  'id',
  'uploadedAt',
  'url',
  'minimalResUrl',
  'lowResUrl',
  'highResUrl',
  'isLocal',
  'thumbDimensions',
  'photo',
  'tileContent',
  'persistenceKeys',
  'width',
  'height',
  'currentAspectRatio',
  'wasUsed',
  'uploadSrc',
  'hashCode',
  'faces',
  'faceDetectionSuccess',
  'stripe',
  'cropperData',
  'tileContent',
  'smallFacesFiltered',
  'type',
  'localUrl',
  'originalFile',
])

const BASE_PATCHABLE_KEYS_WHITELIST = new Set([
  'cropParams',
  'filterImageUrl',
  'isProcessing',
  'processingEffect',
  'ignoreWarnings',
  'metadata',
  'currFilter',
  'hasHighResFilter',
  'prevAspectRatio',
  'ref',
])

export abstract class BaseTileStore {
  readonly photoBankStore: PhotoBankStore

  readonly id: string

  readonly uploadedAt: number

  @observable accessor tileSize: TILE_SIZES

  @observable accessor materialType: MATERIAL_TYPES

  @observable.deep accessor cropParams: Record<number, TileCrop>

  @observable.deep accessor cropperData: CropperData

  @observable accessor frameColor: FRAME_COLORS

  @observable accessor matting: MATTING

  @observable accessor filter: FILTERS

  @observable accessor mattingColor: MatColorKey

  @observable accessor lastFramedColor: FRAME_COLORS

  @observable accessor filterImageUrl: string | null | undefined

  @observable accessor currFilter: FILTERS | undefined

  @observable accessor hasHighResFilter = false

  @observable accessor prevAspectRatio: number | undefined

  @observable accessor isProcessing = false

  @observable accessor processingEffect: string | null | undefined

  @observable.ref accessor ref: unknown | undefined

  @observable accessor ignoreWarnings = false

  @observable accessor metadata: Record<string, unknown> | undefined

  @observable accessor _originalUrl: string | undefined

  @observable.shallow accessor unknownPatchFields = new Map<string, unknown>()

  constructor(data: TileData, photoBankStore: PhotoBankStore) {
    this.photoBankStore = photoBankStore
    this.id = data.id
    this.uploadedAt = data.uploadedAt

    this.tileSize = data.tileSize
    this.materialType = data.materialType
    this.cropParams = data.cropParams
    this.cropperData = data.cropperData

    this.frameColor = data.customStyle?.frameColor ?? FRAME_COLORS.NONE
    this.matting = data.customStyle?.matting ?? MATTING.NONE
    this.filter = data.customStyle?.filter ?? FILTERS.NONE
    this.mattingColor = MattingColorSchema.parse(
      data.customStyle?.mattingColor ?? DEFAULT_MAT_COLOR
    )
    this.lastFramedColor = data.customStyle?.lastFramedColor ?? this.frameColor

    this.filterImageUrl = data.filterImageUrl
    this.currFilter = data.customStyle?.filter ?? FILTERS.NONE
    this.hasHighResFilter = false
    this.prevAspectRatio = undefined
    this.ref = undefined
    this.metadata = data.metadata ?? {}
    this._originalUrl = data.originalUrl
    this.ignoreWarnings = data.ignoreWarnings ?? false
    makeObservable(this)
  }

  @computed
  get originalUrl(): string | undefined {
    return this._originalUrl
  }

  @computed
  get customStyle() {
    return {
      frameColor: isFramedMaterial(this.materialType)
        ? this.frameColor
        : FRAME_COLORS.NONE,
      matting: this.matting,
      filter: this.filter,
      mattingColor: this.mattingColor,
      lastFramedColor: this.lastFramedColor,
    }
  }

  @computed
  get currentAspectRatio(): number {
    const tileStyleData = getTileStyleData({
      customStyle: this.customStyle,
      materialType: this.materialType,
    })

    return getTileCropTargetAspectRatio({
      tileSize: this.tileSize,
      mattingType: tileStyleData.matting,
      materialType: this.materialType,
      tileContent: this.tileContent,
      experimentManager,
    })
  }

  @computed
  get providerSnapshot(): Tile {
    return this._buildProviderSnapshot()
  }

  protected _buildProviderSnapshot(): Tile {
    const result = this.toJSON() as Tile
    result.filterImageUrl = this.filterImageUrl
    result.currFilter = this.currFilter
    result.hasHighResFilter = this.hasHighResFilter
    result.prevAspectRatio = this.prevAspectRatio
    result.ref = this.ref
    result.isProcessing = this.isProcessing
    result.processingEffect = this.processingEffect
    result.ignoreWarnings = this.ignoreWarnings
    result.currentAspectRatio = this.currentAspectRatio

    const fromUnknown: Record<string, unknown> = {}
    this.unknownPatchFields.forEach((value, key) => {
      fromUnknown[key] = toJS(value)
    })
    return { ...fromUnknown, ...result } as Tile
  }

  abstract get tileContent(): TILE_CONTENT

  abstract getPhotoUids(): string[]

  hasValidRequiredPhotos(): boolean {
    return true
  }

  async getPhotoUrls(): Promise<TilePhotoUrl[]> {
    return Promise.all(
      readOutsideReaction(() => this.getPhotoUids()).map(async (uid) => ({
        uid,
        url: await readOutsideReaction(() =>
          this.photoBankStore.getRemotePhotoUrlAsync(uid)
        ),
      }))
    )
  }

  getPersistenceState(): TilePersistenceDisposition {
    return 'skip'
  }

  getCropAdjustmentContext(): TileCropAdjustmentContext | null {
    return null
  }

  get persistenceKeys(): string[] {
    return BASE_PERSISTENCE_KEYS
  }

  @action
  applyPatch(patch: TilePatch, mode: TilePatchMode): void {
    if (mode === 'replace') {
      this.unknownPatchFields.clear()
    }

    for (const [key, value] of Object.entries(patch)) {
      if (IGNORED_UPDATE_PATCH_KEYS.has(key)) {
        const allowCenterpieceResUrls =
          this.tileContent === TILE_CONTENT.CENTERPIECE &&
          (key === 'highResUrl' || key === 'lowResUrl')
        if (!allowCenterpieceResUrls) {
          continue
        }
      }

      if (key === 'tileSize' && value !== undefined) {
        this.updateSize(baseTileFields.tileSize.parse(value))
        continue
      }

      if (key === 'materialType' && value !== undefined) {
        this.applyMaterial(baseTileFields.materialType.parse(value))
        continue
      }

      if (key === 'customStyle' && value) {
        this.applyCustomStylePatch(CustomStyleSchema.parse(value))
        continue
      }

      if (key === 'originalUrl') {
        if (this.applyOriginalUrlPatch(value as string | undefined, mode)) {
          continue
        }
      }

      if (this.applyPatchedField(key, value, mode)) {
        continue
      }

      this.storeUnknownPatchField(key, value, mode)
    }
  }

  protected applyPatchedField(
    key: string,
    value: unknown,
    mode: TilePatchMode
  ): boolean {
    if (!this.getPatchableKeysWhitelist(mode).has(key)) {
      return false
    }

    ;(this as unknown as Record<string, unknown>)[key] = value
    return true
  }

  protected applyCustomStylePatch(
    style: NonNullable<TilePatch['customStyle']>
  ): void {
    if (style.frameColor !== undefined) this.updateFrameColor(style.frameColor)
    if (style.matting !== undefined) this.updateMatting(style.matting)
    if (style.filter !== undefined) this.updateFilter(style.filter)
    if (style.mattingColor !== undefined)
      this.updateMattingColor(style.mattingColor)
    if (style.lastFramedColor !== undefined) {
      this.lastFramedColor = style.lastFramedColor
    }
  }

  protected applyOriginalUrlPatch(
    value: string | undefined,
    _mode: TilePatchMode
  ): boolean {
    this._originalUrl = value
    return true
  }

  protected getPatchableKeysWhitelist(_mode: TilePatchMode): Set<string> {
    return BASE_PATCHABLE_KEYS_WHITELIST
  }

  protected storeUnknownPatchField(
    key: string,
    value: unknown,
    mode: TilePatchMode
  ) {
    logger.warning('Accepted unknown runtime field', {
      mode,
      key,
      tileId: this.id,
    })
    if (value === undefined) {
      this.unknownPatchFields.delete(key)
    } else {
      this.unknownPatchFields.set(key, value)
    }
  }

  @action
  updateSize(tileSize: TILE_SIZES) {
    this.tileSize = tileSize
  }

  @action
  applyMaterial(materialType: MATERIAL_TYPES) {
    const lastFramedColor =
      isFramedMaterial(materialType) || this.frameColor === FRAME_COLORS.NONE
        ? this.lastFramedColor
        : this.frameColor

    this.lastFramedColor = lastFramedColor

    const newFrameColor = isFramedMaterial(materialType)
      ? isFramedMaterial(this.materialType)
        ? this.frameColor
        : this.lastFramedColor
      : FRAME_COLORS.NONE

    this.updateFrameColor(newFrameColor)
    this.materialType = materialType
  }

  @action
  updateCropParams(aspectRatio: number, crop: TileCrop) {
    this.cropParams[aspectRatio] = crop
  }

  @action
  updateFrameColor(color: FRAME_COLORS) {
    this.frameColor = color
  }

  @action
  updateMatting(matting: MATTING) {
    this.matting = matting
  }

  @action
  updateFilter(filter: FILTERS) {
    // If the filter is the same, we don't need to update the filter image url
    if (this.filter === filter) return

    this.filter = filter
    this.filterImageUrl = undefined
  }

  @action
  updateMattingColor(color: MatColorKey) {
    this.mattingColor = color
  }

  @action
  applyFullStyling(style: Partial<StylingFields>) {
    if (style.frameColor !== undefined) this.frameColor = style.frameColor
    if (style.matting !== undefined) this.matting = style.matting
    if (style.filter !== undefined) {
      if (this.filter !== style.filter) {
        this.filterImageUrl = undefined
      }
      this.filter = style.filter
    }
    if (style.mattingColor !== undefined) this.mattingColor = style.mattingColor
    if (style.lastFramedColor !== undefined)
      this.lastFramedColor = style.lastFramedColor
  }

  abstract toTileData(): TileData

  protected baseTileData() {
    return {
      id: this.id,
      uploadedAt: this.uploadedAt,
      tileSize: this.tileSize,
      materialType: this.materialType,
      cropParams: toJS(this.cropParams),
      cropperData: toJS(this.cropperData),
      customStyle: this.customStyle,
      filterImageUrl: this.filterImageUrl,
      ignoreWarnings: this.ignoreWarnings,
      metadata: toJS(this.metadata),
      originalUrl: this.originalUrl,
    }
  }

  toJSON(): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const key of this.persistenceKeys) {
      if (key === 'customStyle') {
        result.customStyle = {
          frameColor: this.customStyle.frameColor,
          matting: this.matting,
          filter: this.filter,
          mattingColor: this.mattingColor,
          lastFramedColor: this.lastFramedColor,
        }
      } else if (key in this) {
        result[key] = toJS((this as unknown as Record<string, unknown>)[key])
      } else {
        logger.warning('Tile has unknown key in toJSON', {
          key,
          tileContent: this.tileContent,
        })
      }
    }
    return result
  }
}
